import { useState } from "react";
import { sendToStudio, sendToContractGen, contractGenUrl, studioUrl } from "../lib/bridge";
import { actions } from "../lib/store";
import { useCan } from "../lib/auth";
import { Icon } from "./Icons";
import { Modal } from "./ui";
import { fmtDate, relative } from "../lib/format";
import type { Project, StudioLinkRef } from "../lib/types";

/* Keeping a project in step with the apps that carry the rest of its life:
   the Contract Generator (the agreement), the Bid Manager (tendering) and the
   Project Manager (construction). A first send creates the job over there; a
   re-sync carries the current facts and register across to the same job. An
   existing job — one made before the project was linked — can be attached by
   hand so nothing gets duplicated. */

type Target = "contract" | "bid" | "pm";

const DESTINATIONS: { key: Target; label: string; blurb: string; icon: "report" | "money" | "layers"; verb: string }[] = [
  { key: "contract", label: "Contract Generator", blurb: "Draft and revise the agreement", icon: "report", verb: "Draft the contract" },
  { key: "bid", label: "Bid Manager", blurb: "Tender the works and take bids", icon: "money", verb: "Create the tender" },
  { key: "pm", label: "Project Manager", blurb: "Run it on site once it's built", icon: "layers", verb: "Create the site project" },
];

/** Compact strip for the dashboard hero — the full panel lives in Sync. */
export function StudioLinks({ p, showCount }: { p: Project; showCount?: boolean }) {
  const canEdit = useCan("contracts");
  const sheets = (p.deliverables ?? []).length;
  const links = p.links ?? {};
  const linked = DESTINATIONS.filter((d) => links[d.key]);
  if (!canEdit && !linked.length) return null;

  return (
    <div className="row wrap" style={{ gap: 8, alignItems: "center" }}>
      {linked.map((d) => {
        const ref = links[d.key]!;
        return (
          <a key={d.key} className="btn ghost sm" href={ref.url} target="_blank" rel="noreferrer"
            title={`Linked ${fmtDate(ref.at)}${ref.sheets ? ` · ${ref.sheets} sheets sent` : ""}`}>
            <Icon.arrowUpRight width={14} />{d.label}{ref.code ? <> · <span className="mono">{ref.code}</span></> : null}
          </a>
        );
      })}
      {canEdit && linked.length < DESTINATIONS.length && (
        <span className="dim" style={{ fontSize: 11.5 }}>
          {showCount && !linked.length
            ? (sheets ? `${sheets} sheets travel with it` : "No register yet — generate the package first")
            : null}
        </span>
      )}
    </div>
  );
}

/** The full panel: every destination, its linked state, and how to sync it. */
export function SyncPanel({ p }: { p: Project }) {
  const canEdit = useCan("contracts");
  const [linkOpen, setLinkOpen] = useState<Target | null>(null);
  const sheets = (p.deliverables ?? []).length;
  const links = p.links ?? {};

  function send(target: Target) {
    if (target === "contract") {
      sendToContractGen(p);
      // the Contract Generator has no link-back channel — record the hand-off here
      actions.setStudioLink(p.id, "contract", {
        id: p.code, code: p.code, url: `${contractGenUrl()}/`, sheets: 0, at: new Date().toISOString(),
      });
      return;
    }
    sendToStudio(p, target);   // the studio posts the created job back and we link it
  }

  return (
    <div className="col" style={{ gap: 14 }}>
      <p className="muted" style={{ fontSize: 14.5, lineHeight: 1.6, maxWidth: 640 }}>
        This project's life carries on in three other rooms. Send it across once, then re-sync whenever the facts or the
        drawing register move on — the same job is updated rather than duplicated. Already made the job over there?
        Link it instead.
      </p>

      {DESTINATIONS.map((d) => {
        const ref = links[d.key];
        return (
          <div key={d.key} className="card pad reveal">
            <div className="row between wrap" style={{ gap: 12 }}>
              <div className="row" style={{ gap: 13, minWidth: 0 }}>
                <span style={{ width: 40, height: 40, borderRadius: 11, background: "var(--paper-2)", color: ref ? "var(--accent-ink)" : "var(--ink-3)", display: "grid", placeItems: "center", flexShrink: 0 }}>
                  {Icon[d.icon]({ width: 19 })}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="row wrap" style={{ gap: 8, alignItems: "baseline" }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{d.label}</span>
                    {ref
                      ? <span className="badge ok" style={{ fontSize: 9 }}><Icon.check width={9} />Linked</span>
                      : <span className="badge" style={{ fontSize: 9 }}>Not linked</span>}
                  </div>
                  <div className="dim" style={{ fontSize: 12.5, marginTop: 2 }}>
                    {ref
                      ? <>{ref.code ? <span className="mono">{ref.code}</span> : null}{ref.code ? " · " : ""}last synced {relative(ref.at)}{ref.sheets ? ` · ${ref.sheets} sheets sent` : ""}</>
                      : d.blurb}
                  </div>
                </div>
              </div>

              {canEdit && (
                <div className="row wrap" style={{ gap: 8, flexShrink: 0 }}>
                  {ref && <a className="btn ghost sm" href={ref.url} target="_blank" rel="noreferrer"><Icon.arrowUpRight width={13} />Open</a>}
                  <button className={"btn sm " + (ref ? "soft" : "accent")} onClick={() => send(d.key)}>
                    <Icon.swap width={13} />{ref ? "Re-sync" : d.verb}
                  </button>
                  {ref
                    ? <button className="chip" style={{ fontSize: 11 }} onClick={() => actions.unlinkStudioJob(p.id, d.key)}>Unlink</button>
                    : <button className="chip" style={{ fontSize: 11 }} onClick={() => setLinkOpen(d.key)}>Link existing</button>}
                </div>
              )}
            </div>
            {!ref && d.key !== "contract" && (
              <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
                {sheets ? `${sheets} sheets from the register travel with it.` : "No drawing register yet — generate the package first and it'll travel across."}
              </div>
            )}
          </div>
        );
      })}

      {linkOpen && <LinkExistingModal p={p} target={linkOpen} onClose={() => setLinkOpen(null)} />}
    </div>
  );
}

/** Attach a job that already exists over there, so a project that was set up
    before the bridge doesn't get a duplicate. */
function LinkExistingModal({ p, target, onClose }: { p: Project; target: Target; onClose: () => void }) {
  const dest = DESTINATIONS.find((d) => d.key === target)!;
  const [code, setCode] = useState("");
  const [url, setUrl] = useState("");
  const base = target === "contract" ? contractGenUrl() : studioUrl();

  function save() {
    const trimmed = url.trim();
    const ref: StudioLinkRef = {
      id: code.trim() || trimmed || p.code,
      code: code.trim(),
      url: trimmed || `${base}/`,
      sheets: 0,
      at: new Date().toISOString(),
    };
    actions.setStudioLink(p.id, target, ref);
    onClose();
  }

  return (
    <Modal open onClose={onClose} title={`Link the existing ${dest.label} job`}>
      <div className="col" style={{ gap: 13 }}>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
          If <b>{p.name}</b> already exists in the {dest.label}, point this project at it rather than creating a second
          one. Paste its reference and the link you use to open it.
        </p>
        <div className="field"><label>Reference <span className="dim">(its code over there)</span></label>
          <input className="input mono" autoFocus value={code} onChange={(e) => setCode(e.target.value)}
            placeholder={target === "bid" ? "TDR-2603" : target === "pm" ? "PRJ-2601" : p.code} />
        </div>
        <div className="field"><label>Link <span className="dim">(optional)</span></label>
          <input className="input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={`${base}/…`} />
        </div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" onClick={save} disabled={!code.trim() && !url.trim()}><Icon.check width={15} />Link it</button>
        </div>
      </div>
    </Modal>
  );
}
